import { faqAnswerToText, type Faq } from "@/lib/faqs";
import { SITE_URL } from "@/lib/config";

const pickFirstNonEmptyString = (...values: Array<string | null | undefined>) =>
    values.find(
        (value) => typeof value === "string" && value.trim().length > 0,
    ) ?? "";

export function buildToolJsonLd(
    title: string,
    description: string,
    path: string,
): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: title,
        applicationCategory: "MultimediaApplication",
        operatingSystem: "Web",
        url: `${SITE_URL}${path}`,
        description,
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description: "Free trial available",
        },
        provider: {
            "@type": "Organization",
            name: "Pixro AI",
            url: SITE_URL,
        },
    };
}

export function buildHomeJsonLd(faqs?: Faq[]): Record<string, unknown> {
    const graph: Record<string, unknown>[] = [
        {
            "@type": "Organization",
            "@id": `${SITE_URL}/#org`,
            name: "Pixro AI",
            url: SITE_URL,
            logo: `${SITE_URL}/web-app-manifest-512x512.png`,
            sameAs: [
                "https://x.com/pixroai",
                "https://in.linkedin.com/company/pixroai",
                "https://www.instagram.com/pixroai/",
                "https://www.youtube.com/@PixroAI",
            ],
        },
        {
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            url: SITE_URL,
            name: "Pixro AI",
            publisher: {
                "@id": `${SITE_URL}/#org`,
            },
            potentialAction: {
                "@type": "SearchAction",
                target: `${SITE_URL}/blogs?q={search_term_string}`,
                "query-input": "required name=search_term_string",
            },
        },
        {
            "@type": "SoftwareApplication",
            "@id": `${SITE_URL}/#software`,
            name: "Pixro AI",
            url: SITE_URL,
            description:
                "Pixro AI is a web-based creative studio for generating product photos, videos, fashion visuals, and ecommerce marketing assets with AI.",
            applicationCategory: "DesignApplication",
            operatingSystem: "Web",
            publisher: {
                "@id": `${SITE_URL}/#org`,
            },
            offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
            },
        },
        {
            "@type": "HowTo",
            name: "How Pixro AI Works",
            description:
                "Generate studio-quality product visuals and videos in three simple steps with Pixro AI.",
            step: [
                {
                    "@type": "HowToStep",
                    position: 1,
                    name: "Start with your product",
                    text: "Drop in a product photo and choose the type of visuals you need.",
                },
                {
                    "@type": "HowToStep",
                    position: 2,
                    name: "Watch it transform",
                    text: "Generate photoshoots, videos, and creative assets in seconds.",
                },
                {
                    "@type": "HowToStep",
                    position: 3,
                    name: "Refine and create more",
                    text: "Iterate, explore styles, and generate variations effortlessly.",
                },
            ],
        },
    ];

    if (faqs && faqs.length > 0) {
        graph.push({
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: faqAnswerToText(f.a),
                },
            })),
        });
    }

    return {
        "@context": "https://schema.org",
        "@graph": graph,
    };
}

export function buildPricingJsonLd(): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Pixro AI",
        offers: [
            {
                "@type": "Offer",
                name: "Free Trial",
                price: "0",
                priceCurrency: "USD",
                description: "5 credits, no credit card required",
            },
            {
                "@type": "Offer",
                name: "Trial Monthly",
                price: "10",
                priceCurrency: "USD",
                description: "50 credits for small brands testing the waters",
            },
            {
                "@type": "Offer",
                name: "Growth Monthly",
                price: "30",
                priceCurrency: "USD",
                description: "400 credits for growing D2C fashion brands",
            },
        ],
    };
}

export function buildDukaanAdsJsonLd(
    faqs: { q: string; a: string }[],
    path = "/dukaan-ads",
): Record<string, unknown> {
    const graph: Record<string, unknown>[] = [
        {
            "@type": "Service",
            "@id": `${SITE_URL}${path}#service`,
            name: "Dukaan Ads",
            serviceType: "Social media ads for local shops",
            areaServed: "IN",
            description:
                "Dukaan Ads छोटे दुकानदारों के लिए रोज़ तैयार professional विज्ञापन (फोटो + वीडियो) बनाता है, जो WhatsApp, Instagram और Facebook पर लगाए जा सकते हैं।",
            provider: {
                "@type": "Organization",
                name: "Pixro AI",
                url: SITE_URL,
            },
            offers: [
                {
                    "@type": "Offer",
                    name: "Starter",
                    price: "499",
                    priceCurrency: "INR",
                    description: "12 विज्ञापन (12 फोटो) हर महीने",
                },
                {
                    "@type": "Offer",
                    name: "Pro",
                    price: "999",
                    priceCurrency: "INR",
                    description: "24 विज्ञापन (22 फोटो + 2 वीडियो) हर महीने",
                },
                {
                    "@type": "Offer",
                    name: "Premium",
                    price: "1999",
                    priceCurrency: "INR",
                    description: "30 विज्ञापन (26 फोटो + 4 वीडियो) हर महीने",
                },
            ],
        },
    ];

    if (faqs && faqs.length > 0) {
        graph.push({
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        });
    }

    return { "@context": "https://schema.org", "@graph": graph };
}

export function buildFaqJsonLd(
    faqs: { q: string; a: string }[],
): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: faqAnswerToText(f.a),
            },
        })),
    };
}

export function buildHowToJsonLd(
    name: string,
    steps: { title: string; body: string }[],
): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name,
        step: steps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.body,
        })),
    };
}

export function buildBlogJsonLd(post: {
    title: string;
    slug: string;
    excerpt?: string;
    metaDescription?: string;
    featuredImage?: string;
    ogImageUrl?: string;
    createdAt: string;
    updatedAt?: string;
    author?: string;
}): Record<string, unknown> {
    const image = pickFirstNonEmptyString(post.ogImageUrl, post.featuredImage);
    const description = pickFirstNonEmptyString(
        post.metaDescription,
        post.excerpt,
    );

    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        // Google rejects empty-string values for image/description, so only
        // include them when we actually have content.
        ...(description ? { description } : {}),
        ...(image ? { image } : {}),
        url: `${SITE_URL}/blogs/${post.slug}`,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}/blogs/${post.slug}`,
        },
        datePublished: post.createdAt,
        dateModified: post.updatedAt ?? post.createdAt,
        author: {
            "@type": "Person",
            name: post.author ?? "Pixro AI",
        },
        publisher: {
            "@type": "Organization",
            name: "Pixro AI",
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/web-app-manifest-512x512.png`,
            },
        },
    };
}

export function buildBreadcrumbJsonLd(
    items: { name: string; path: string }[],
): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            item: `${SITE_URL}${item.path}`,
        })),
    };
}

export function buildCaseStudyJsonLd(study: {
    brand: string;
    slug: string;
    headline: string;
    summary: string;
    hero?: string;
    publishedAt: string;
}): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `${study.brand}: ${study.headline}`,
        description: study.summary,
        image: study.hero ? `${SITE_URL}${study.hero}` : undefined,
        url: `${SITE_URL}/case-studies/${study.slug}`,
        datePublished: study.publishedAt,
        author: {
            "@type": "Organization",
            name: "Pixro AI",
            url: SITE_URL,
        },
        publisher: {
            "@type": "Organization",
            name: "Pixro AI",
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/web-app-manifest-512x512.png`,
            },
        },
    };
}

export function buildCaseStudyListJsonLd(
    studies: { brand: string; slug: string }[],
): Record<string, unknown> {
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: studies.map((study, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: study.brand,
            url: `${SITE_URL}/case-studies/${study.slug}`,
        })),
    };
}
